import { createClient, type SupabaseClient } from '@supabase/supabase-js';

/**
 * Vite browser Supabase client.
 *
 * Creates a single per-session Supabase client using VITE_SUPABASE_URL
 * and VITE_SUPABASE_ANON_KEY. Replaces the `@supabase/ssr` browser
 * client pattern — the plain `@supabase/supabase-js` client is enough
 * for a SPA, since we no longer rely on cookie-based auth.
 *
 * Usage: `import { getSupabaseBrowserClient } from '@/lib/supabase/browser'`
 */

// This file is imported from three contexts:
//   1. The Vite browser build — the vite.config.mts define shim replaces
//      process.env.NEXT_PUBLIC_* with the VITE_* string literals at build
//      time, so this module sees real values.
//   2. The Next.js build — process.env.NEXT_PUBLIC_* is natively populated.
//   3. Jest / ts-node — reads from actual process.env at runtime.
//
// In all three cases, reading process.env.NEXT_PUBLIC_* is the safe
// common denominator. We deliberately avoid `import.meta.env` because
// Jest's CJS runtime cannot parse it.
const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || '';

if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  throw new Error(
    'Missing Supabase env vars. Set NEXT_PUBLIC_SUPABASE_URL and NEXT_PUBLIC_SUPABASE_ANON_KEY (or VITE_SUPABASE_URL/VITE_SUPABASE_ANON_KEY which the Vite define shim maps to the same names).',
  );
}

let client: SupabaseClient | null = null;

/**
 * Get the singleton Supabase browser client.
 *
 * Kept as a factory (rather than a module-level const) so tests can
 * reset it and so the client is only initialised when actually used.
 */
export function getSupabaseBrowserClient(): SupabaseClient {
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        flowType: 'pkce',
      },
    });
  }
  return client;
}

/**
 * Reset the singleton. Only used by tests.
 */
export function resetSupabaseBrowserClient(): void {
  client = null;
}
